/**
 * State-group transform capture — the Properties analog of
 * captureTransformForKeyframe (keyframe-capture.ts).
 *
 * When a group state is active (not Base), a gizmo/transform drag should be
 * stored as a TransformOverride DELTA in that state — not folded into the
 * object's authored pose. This computes the live-vs-authored delta (identical
 * math to the keyframe path) and routes it through the shared
 * `setStateTransform` action, which seeds the member if needed and commits with
 * undo.
 *
 * Returns true if the drag was captured into a state (caller skips the Base fold
 * + normal TransformCommand), false if the object is at Base (edits → authored).
 */

import type { SceneObject } from 'trace-viewer/scene';
import type { PropertyValue, PropertiesData } from 'trace-viewer/behaviors';
import { Properties } from 'trace-viewer/behaviors';
import type { UndoCommand } from 'trace-viewer/editor';
import {
    editTargetState,
    transformEditTargetState,
    setStateTransform,
    setActive,
    captureStateValue,
    getUndoManager,
    CompositeCommand,
    liveLocalPose,
    pageCaptureContext,
    capturePageState,
    type PageCaptureContext,
} from 'trace-viewer/editor';
import { isStatesEditingEnabled } from './states-editing-mode';
import { pageEditing } from './page-editing-mode';
import { pushCommand } from './behaviors/pagesSharedActions';
import { captureActiveForKeyframe, getKeyframeCaptureContext } from './keyframe-capture';

/**
 * True when `obj` has a non-Base active group state that a transform edit should
 * be routed into. Mirrors getKeyframeCaptureContext's role for keyframes.
 */
export function hasActiveStateForTransform(obj: SceneObject): boolean {
    if (!isStatesEditingEnabled()) return false;
    // Transform-specific resolver: a RIG drag always edits placement (authored),
    // so it reports no active state here and the caller takes the normal
    // TransformCommand + base-fold path.
    return transformEditTargetState(obj) !== undefined;
}

/**
 * Capture a completed drag's final pose into the active group state as an
 * ABSOLUTE local pose. No-op (returns false) when the object is at Base. No delta
 * math — just the live local pose; the state replaces the transform wholesale, so
 * this is robust regardless of the authored base (kills the unseeded-child
 * doubling bug).
 */
export function captureTransformForState(obj: SceneObject): boolean {
    if (!isStatesEditingEnabled()) return false;
    if (transformEditTargetState(obj) === undefined) return false;
    // Fire-and-forget like the keyframe path: the action seeds + commits + undo.
    void setStateTransform(obj, liveLocalPose(obj));
    return true;
}

/**
 * Show or hide `obj` where it is seen: a selected keyframe takes it (the
 * sandbox's keyframe capture, which applies a moment later), else
 * trace-viewer's `setActive` writes it into the showing page for shared
 * content, the showing state, or Base, as one undo entry. Says `'keyframe'`
 * when the keyframe took it. Never rejects: a refusal (the state a page
 * assigns, with page editing off) or a fault is logged and changes nothing.
 */
export async function toggleActive(obj: SceneObject, active: boolean): Promise<'keyframe' | 'shown'> {
    try {
        if (captureActiveForKeyframe(obj, active)) return 'keyframe';
        const result = await setActive([{ object: obj, active }], { pageEditing });
        pushCommand(result.command);
        for (const refusal of result.refused) console.warn(`[Sandbox] ${refusal.message}`);
    } catch (error) {
        console.error(`[Sandbox] showing or hiding "${obj.name}" failed:`, error);
    }
    return 'shown';
}

/**
 * Reconcile the `states` map for a Properties commit so a PROPERTY edit never
 * clobbers state overrides written externally (e.g. a transform the gizmo wrote
 * into the active state) via a stale panel `localData.states`.
 *
 * `next` is the data the panel is about to commit; `cached` is the panel's last
 * working copy; `live` is the behavior's current authored data. If the panel
 * isn't itself changing states (next.states deep-equals cached.states), we
 * replace next.states with the LIVE states — preserving any external override.
 * When the panel IS editing states, its `next.states` wins unchanged.
 */
export function reconcilePropertyEditStates(
    next: PropertiesData,
    cached: PropertiesData,
    live: PropertiesData,
): PropertiesData {
    const editingStates = JSON.stringify(next.states) !== JSON.stringify(cached.states);
    if (editingStates) return next;
    return { ...next, states: live.states };
}

/**
 * Overlay the active group state's value overrides onto a Properties data
 * snapshot's declarations, so the behaviors panel's fields show the STATE's
 * value (not Base) while a state is active. The Properties analog of
 * overlayKeyframeValues. Returns `data` unchanged at Base / non-Properties.
 *
 * `obj` is the object whose panel section is rendering; the active state is the
 * group's (resolved via the same routing as edits), and its override for `obj`
 * lives in `obj`'s own `states[active].values`.
 */
export function overlayStateValues<T extends { '@type': string; properties?: { name: string; value?: PropertyValue }[] }>(
    obj: SceneObject,
    data: T,
): T {
    if (data['@type'] !== 'Properties' || !data.properties) return data;
    if (!isStatesEditingEnabled()) return data;
    const target = editTargetState(obj);
    if (target === undefined) return data;
    const props = obj.getBehavior(Properties);
    const values = props?.getData().states?.[target]?.values;
    if (!values) return data;
    return {
        ...data,
        properties: data.properties.map((decl) =>
            decl.name in values ? { ...decl, value: values[decl.name] } : decl,
        ),
    };
}

// --- Property VALUE capture into the showing state ------------------------

/**
 * The declaration values a Properties panel edit changed, or null when it also
 * changed anything else (a declaration added, removed, renamed, reordered or
 * retyped, or the states map). `shown` is what the panel displayed, so values
 * a state overlays onto the fields don't count as edits.
 */
function changedDeclarationValues(shown: PropertiesData, next: PropertiesData): Record<string, PropertyValue> | null {
    // The panel writes a missing states map back as `{}`.
    const shape = ({ properties, states, ...rest }: PropertiesData) =>
        JSON.stringify([properties.map(({ value: _value, ...decl }) => decl), states ?? {}, rest]);
    if (shape(shown) !== shape(next)) return null;
    const changed: Record<string, PropertyValue> = {};
    next.properties.forEach((decl, i) => {
        if (decl.value !== undefined && JSON.stringify(decl.value) !== JSON.stringify(shown.properties[i].value)) {
            changed[decl.name] = decl.value;
        }
    });
    return changed;
}

/**
 * `next` with each declaration value the edit left alone set back to Base's
 * own value. While a state is showing, the panel's fields hold that state's
 * values, and a structural edit committed as-is would write them into Base.
 *
 * Declarations pair by position when the edit kept their count and changed a
 * name in place (a rename, committed keystroke by keystroke, can pass through
 * a name that already exists). An add, a remove or a pure reorder pairs them
 * by name.
 */
function keepBaseValues(shown: PropertiesData, next: PropertiesData, base: PropertiesData): PropertiesData {
    const names = (d: PropertiesData) => d.properties.map((p) => p.name);
    const sorted = (d: PropertiesData) => JSON.stringify([...names(d)].sort());
    const byPosition = next.properties.length === shown.properties.length
        && JSON.stringify(names(shown)) === JSON.stringify(names(base))
        && sorted(next) !== sorted(shown);
    const shownByName = new Map(shown.properties.map((d) => [d.name, d]));
    const baseByName = new Map(base.properties.map((d) => [d.name, d]));
    return {
        ...next,
        properties: next.properties.map((decl, i) => {
            const was = byPosition ? shown.properties[i] : shownByName.get(decl.name);
            const own = byPosition ? base.properties[i] : baseByName.get(decl.name);
            return was && own && JSON.stringify(was.value) === JSON.stringify(decl.value)
                ? ({ ...decl, value: own.value } as typeof decl)
                : decl;
        }),
    };
}

/** Where a Properties panel edit goes: captured into a state, or committed to Base. */
export type PropertyEditRoute =
    /**
     * Written into the showing state. False means the engine refused it (Play):
     * values written before the refusal stay, as one undo entry. `show` is what
     * the panel holds afterwards: the edit over the live states map, or what
     * is showing when refused.
     */
    | { captured: boolean; show: PropertiesData }
    /** Commit `base` to Base; `target` is the state showing, if any, whose values the panel then shows. */
    | { base: PropertiesData; target?: string };

/** The showing page's context for a property edit on `obj`, only once that page has keyed it: a value edit does not key a page. */
function keyedPageContext(obj: SceneObject): PageCaptureContext | null {
    const page = pageCaptureContext(obj, pageEditing);
    return page?.assignedState && obj.getBehavior(Properties)?.hasState(page.assignedState) ? page : null;
}

/**
 * Route a Properties panel edit on `obj`. `shown` is what the panel displayed
 * and `next` the edited data. A selected keyframe outranks everything, so the
 * edit goes to Base for the keyframe capture. Otherwise, on a shared child of a
 * Pages container the showing page has keyed, the values go into that page's
 * state, forking one another page shares; this comes before a showing state,
 * as for a drag. Otherwise, while a state is showing, a value edit is written
 * into it, so Base keeps its values: in a group state the engine's
 * `captureStateValue` seeds a bare member, refuses in Play and builds the undo
 * command, and the edit pushes one undo entry. A structural edit goes to Base
 * without the state's values.
 */
export async function routePropertyEditIntoState(
    obj: SceneObject,
    shown: PropertiesData,
    next: PropertiesData,
): Promise<PropertyEditRoute> {
    const props = obj.getBehavior(Properties);
    if (!props) return { base: next };
    // Read before any commit: the commits below change the data the routing reads.
    const page = keyedPageContext(obj);
    const stateTarget = !page && isStatesEditingEnabled() ? editTargetState(obj) : undefined;
    const target = page?.assignedState ?? stateTarget;
    if (target === undefined || getUndoManager().isUndoRedoing || getKeyframeCaptureContext(obj)) {
        return { base: next, target };
    }

    const values = changedDeclarationValues(shown, next);
    if (!values) return { base: keepBaseValues(shown, next, props.getData()), target };
    if (page) {
        if (Object.keys(values).length > 0) {
            const command = await capturePageState(page, `Set ${Object.keys(values).join(', ')} on ${page.pageName}`,
                state => ({ ...state, values: { ...state.values, ...values } }));
            pushCommand(command);
        }
        return { captured: true, show: { ...next, states: props.getData().states } };
    }
    const commands: UndoCommand[] = [];
    let captured = true;
    for (const [name, value] of Object.entries(values)) {
        const result = await captureStateValue(obj, name, value, { record: false });
        if (!result.captured) {
            captured = false;
            break;
        }
        if (result.command) commands.push(result.command);
    }
    if (commands.length > 0) {
        getUndoManager().push(commands.length === 1 ? commands[0] : new CompositeCommand('Set state values', commands));
    }
    const live = props.getData();
    return { captured, show: captured ? { ...next, states: live.states } : overlayStateValues(obj, live) };
}
